<template>
  <div>
    <!-- Breadcrumbs -->
    <nav class="mt-5 pt-5 ps-3" aria-label="breadcrumb">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
        <li class="breadcrumb-item"><router-link to="/admin/companies">Companies</router-link></li>
        <li class="breadcrumb-item active" aria-current="page">Company Details</li>
      </ol>
    </nav>

    <div class="container-fluid" v-if="company">
      <!-- Company Header Info -->
      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card bg-primary text-white border-0 shadow-sm">
            <div class="card-body d-flex align-items-center p-4">
              <img
                v-if="company.logo"
                :src="company.logo"
                class="rounded-circle border border-white border-4 me-4"
                style="width: 100px; height: 100px; object-fit: cover"
              />
              <div
                v-else
                class="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center me-4"
                style="width: 100px; height: 100px"
              >
                <i class="fa fa-building fa-3x"></i>
              </div>
              <div>
                <h2 class="mb-1">{{ company.title }}</h2>
                <p class="mb-0 opacity-75">
                  <i class="fa fa-link me-1"></i> {{ company.slug }} | 
                  <i class="fa fa-users me-1"></i> {{ company.company_size }} Employees | 
                  <i class="fa fa-calendar me-1"></i> Joined {{ formatDate(company.createdAt) }}
                </p>
                <div class="mt-2">
                   <span :class="['badge bg-light text-dark me-2', getStatusBadge(company.status)]">
                    Status: {{ company.status }}
                  </span>
                   <span class="badge bg-light text-dark">
                    Billing: {{ company.billing_status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="card border-0 shadow-sm text-center p-3">
            <h3 class="text-primary">{{ members.length }}</h3>
            <small class="text-muted text-uppercase fw-bold">Members</small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-0 shadow-sm text-center p-3">
            <h3 class="text-success">{{ workspaces.length }}</h3>
            <small class="text-muted text-uppercase fw-bold">Workspaces</small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-0 shadow-sm text-center p-3">
            <h3 class="text-info">{{ company.settings?.max_workspaces || '∞' }}</h3>
            <small class="text-muted text-uppercase fw-bold">Max Workspaces</small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-0 shadow-sm text-center p-3">
            <h3 class="text-warning">{{ formatTimeAgo(company.last_activity_at) }}</h3>
            <small class="text-muted text-uppercase fw-bold">Last Active</small>
          </div>
        </div>
      </div>

      <!-- Tabs for detailed info -->
      <div class="row">
        <div class="col-md-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white">
              <ul class="nav nav-tabs card-header-tabs" role="tablist">
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'general' }"
                    @click="activeTab = 'general'"
                  >General Info</button>
                </li>
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'members' }"
                    @click="activeTab = 'members'"
                  >Members ({{ members.length }})</button>
                </li>
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'workspaces' }"
                    @click="activeTab = 'workspaces'"
                  >Workspaces ({{ workspaces.length }})</button>
                </li>
              </ul>
            </div>
            <div class="card-body p-4">
              <!-- General Tab -->
              <div v-if="activeTab === 'general'">
                 <div class="row">
                   <div class="col-md-6">
                      <h6>Company Information</h6>
                      <table class="table table-sm mt-3">
                        <tr>
                          <td class="fw-bold text-muted" style="width: 150px">Title:</td>
                          <td>{{ company.title }}</td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-muted">Slug:</td>
                          <td><code>{{ company.slug }}</code></td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-muted">Owner ID:</td>
                          <td>{{ company.owner_id }}</td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-muted">Size:</td>
                          <td>{{ company.company_size }}</td>
                        </tr>
                      </table>
                   </div>
                   <div class="col-md-6 border-start">
                      <h6>Lifecycle & Billing</h6>
                      <table class="table table-sm mt-3">
                        <tr>
                          <td class="fw-bold text-muted" style="width: 150px">Status:</td>
                          <td>{{ company.status }}</td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-muted">Billing Status:</td>
                          <td>{{ company.billing_status }}</td>
                        </tr>
                        <tr>
                          <td class="fw-bold text-muted">Active:</td>
                          <td>{{ company.is_active ? 'Yes' : 'No' }}</td>
                        </tr>
                        <tr v-if="!company.is_active">
                          <td class="fw-bold text-muted">Deactivated At:</td>
                          <td>{{ company.deactivated_at ? formatDate(company.deactivated_at) : '-' }}</td>
                        </tr>
                      </table>
                   </div>
                 </div>
              </div>

              <!-- Members Tab -->
              <div v-if="activeTab === 'members'">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="member in members" :key="member._id">
                        <td>
                          <div class="d-flex align-items-center">
                             <img :src="member.avatar || 'https://ui-avatars.com/api/?name='+member.name" class="rounded-circle me-2" style="width: 30px; height: 30px" />
                             {{ member.name }}
                          </div>
                        </td>
                        <td>{{ member.email }}</td>
                        <td><span class="badge bg-light text-dark">{{ member.company_role || 'Member' }}</span></td>
                        <td>{{ formatDate(member.createdAt) }}</td>
                      </tr>
                      <tr v-if="members.length === 0">
                        <td colspan="4" class="text-center py-4 text-muted">No members found</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Workspaces Tab -->
              <div v-if="activeTab === 'workspaces'">
                <div class="row g-3">
                   <div class="col-md-4" v-for="ws in workspaces" :key="ws._id">
                      <div class="card h-100 border shadow-none">
                        <div class="card-body">
                           <div class="d-flex justify-content-between align-items-start mb-2">
                             <h6 class="mb-0">{{ ws.title }}</h6>
                             <span :class="['badge small', ws.is_active ? 'bg-success' : 'bg-secondary']">
                               {{ ws.is_active ? 'Active' : 'Inactive' }}
                             </span>
                           </div>
                           <p class="small text-muted mb-3">{{ ws.description || 'No description provided.' }}</p>
                           <div class="d-flex justify-content-between align-items-center mt-auto">
                              <small class="text-muted"><i class="fa fa-users me-1"></i> {{ ws.memberCount || 0 }} members</small>
                              <button class="btn btn-sm btn-link text-primary p-0">View Details</button>
                           </div>
                        </div>
                      </div>
                   </div>
                   <div class="col-12 text-center py-5 text-muted" v-if="workspaces.length === 0">
                     No workspaces found for this company.
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div class="container-fluid text-center py-5" v-else-if="isLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Fetching company details...</p>
    </div>

    <!-- Error State -->
    <div class="container-fluid py-5" v-else>
      <div class="alert alert-danger text-center">
        <i class="fa fa-exclamation-triangle me-2"></i> {{ error || 'Company not found' }}
        <div class="mt-3">
           <router-link to="/admin/companies" class="btn btn-outline-danger">Back to Companies</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import apiService from '@/services/apiService';

const route = useRoute();
const companyId = route.params.id as string;

const company = ref<any>(null);
const members = ref<any[]>([]);
const workspaces = ref<any[]>([]);
const isLoading = ref(true);
const error = ref<string | null>(null);
const activeTab = ref('general');

const loadData = async () => {
  isLoading.value = true;
  try {
    const [companyRes, membersRes, workspacesRes] = await Promise.all([
      apiService.getCompanyById(companyId),
      apiService.getCompanyMembers(companyId),
      apiService.getCompanyWorkspaces(companyId)
    ]);

    company.value = companyRes;
    members.value = Array.isArray(membersRes) ? membersRes : (membersRes.data || []);
    workspaces.value = Array.isArray(workspacesRes) ? workspacesRes : (workspacesRes.data || []);
  } catch (err: any) {
    error.value = err.message || 'Failed to load company data';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatTimeAgo = (dateString: string) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 3600 * 24));
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  return `${diffInDays} days ago`;
};

const getStatusBadge = (status: string) => {
  if (!status) return '';
  switch (status.toLowerCase()) {
    case 'active': return 'border-success text-success';
    case 'inactive': return 'border-warning text-warning';
    case 'suspended': return 'border-danger text-danger';
    default: return 'border-secondary text-secondary';
  }
};

onMounted(() => {
  loadData();
});
</script>

<style scoped>
.nav-tabs .nav-link {
  color: #6c757d;
  font-weight: 500;
  border: none;
  padding: 1rem 1.5rem;
}
.nav-tabs .nav-link.active {
  color: #007bff;
  border-bottom: 2px solid #007bff;
  background: transparent;
}
.card {
  border-radius: 12px;
}
</style>
